import type { Context } from "../../command/context.ts";
import type { HttpOptions } from "../../http/http.ts";
import { unthunk, type Thunk } from "../../util/function.ts";
import type { Environment } from "../app.ts";
import { Client } from "../client.ts";
import { getOperationName, type GraphQLQuery } from "../edit/operation.ts";
import { ClientError } from "../error.ts";

export class Api {
  /**
   * The {@linkcode Context} that was used to create this instance.
   */
  readonly ctx: Context;

  /**
   * The client used to make requests to Gadget's /api/graphql
   * endpoint.
   */
  #client: Client;

  constructor(ctx: Context, environment: Environment) {
    this.ctx = ctx.child({ name: "api" });
    this.#client = new Client(this.ctx, environment, "/api/graphql");
  }

  /**
   * Execute a GraphQL query.
   *
   * @param request - The query and variables to send to the server.
   * @param request.query - The GraphQL query to execute.
   * @param request.variables - The variables to send to the server.
   * @param request.http - {@linkcode HttpOptions} to pass to http.
   * @returns The data returned by the server.
   */
  async query<Query extends GraphQLQuery>({
    query,
    variables,
    ...options
  }: {
    query: Query;
    variables?: Thunk<Query["Variables"]> | null;
    http?: HttpOptions;
  }): Promise<Query["Data"]> {
    const name = getOperationName(query);

    const ctx = this.ctx.child({
      name: "api",
      fields: { edit: { query: name } },
      devFields: { edit: { query: name, variables: unthunk(variables) } },
    });

    ctx.log.info("executing graphql query");
    const response = await this.#client.execute(ctx, {
      operation: query,
      variables,
      ...options,
      http: {
        retry: {
          // queries _should_ be idempotent, so automatically retry them
          methods: ["POST"],
        },
        ...options.http,
      },
    });

    if (response.errors) {
      throw new ClientError(query, response.errors);
    }

    if (!response.data) {
      throw new ClientError(query, "Query response did not contain data");
    }

    return response.data;
  }

  /**
   * Close the client.
   */
  async dispose(): Promise<void> {
    await this.#client.dispose();
  }
}
